import * as i0 from '@angular/core';
import { inject, Injectable, Component, Input, InjectionToken, HostBinding, NgModule } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgIf, CommonModule } from '@angular/common';
import * as i1 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { SolidIconLoader, RegularIconLoader, LightIconLoader } from '@virginmediao2/momentum-icons';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { of, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const ICON_STATE = makeStateKey('MO.ICON-CACHE');

class IconCacheService {
    constructor() {
        this.transferState = inject(TransferState, { optional: true });
        this.iconCache = this.transferState ? this.transferState?.get(ICON_STATE, {}) : {};
    }
    set(iconSet, iconName, icon) {
        this.iconCache[`${iconName}-${iconSet}`] = icon;
        this.transferState?.set(ICON_STATE, this.iconCache);
    }
    has(iconSet, iconName) {
        return Boolean(this.iconCache[`${iconName}-${iconSet}`]);
    }
    get(iconSet, iconName) {
        return this.iconCache[`${iconName}-${iconSet}`];
    }
    get length() {
        return Object.keys(this.iconCache).length;
    }
}
IconCacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconCacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
IconCacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconCacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconCacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const solidIconLoader = new SolidIconLoader();
const regularIconLoader = new RegularIconLoader();
const lightIconLoader = new LightIconLoader();
const getSetLoader = (iconSet) => {
    switch (iconSet) {
        case 'regular':
            return regularIconLoader;
        case 'solid':
            return solidIconLoader;
        case 'light':
            return lightIconLoader;
    }
};
const importIcon = (icon, iconSet) => {
    const iconSetLoader = getSetLoader(iconSet);
    const loader = iconSetLoader[icon];
    if (!loader) {
        throw new Error(`Icon ${icon} not found in icon set ${iconSet}`);
    }
    return loader();
};
class LazyAwesomeComponent {
    constructor() {
        this.iconCache = inject(IconCacheService);
    }
    ngOnInit() {
        const { icon, iconSet } = this;
        return importIcon(icon, iconSet).then(({ definition }) => {
            this.iconCache.set(iconSet, icon, definition);
            this.faIcon = definition;
        });
    }
}
LazyAwesomeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: LazyAwesomeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LazyAwesomeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.0", type: LazyAwesomeComponent, isStandalone: true, selector: "vo-lazy-awesome", inputs: { icon: "icon", iconSet: "iconSet" }, ngImport: i0, template: `
    <fa-icon
      data-testid="lazy-awesome-icon"
      *ngIf="faIcon"
      [icon]="faIcon" />`, isInline: true, dependencies: [{ kind: "ngmodule", type: FontAwesomeModule }, { kind: "component", type: i1.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "spin", "pulse", "mask", "styles", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "classes", "transform", "a11yRole"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: LazyAwesomeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vo-lazy-awesome',
                    standalone: true,
                    imports: [FontAwesomeModule, NgIf],
                    template: `
    <fa-icon
      data-testid="lazy-awesome-icon"
      *ngIf="faIcon"
      [icon]="faIcon" />`,
                }]
        }], propDecorators: { icon: [{
                type: Input
            }], iconSet: [{
                type: Input
            }] } });

const ICON_COMPONENT = new InjectionToken('IconComponent');

class IconComponent {
    constructor() {
        this.hbClassIcon = true;
        this.hbAttrAriaLabel = null;
        /** Hide from accessibility tree */
        this.ariaHidden = null;
        /** Icon size */
        this.size = 'md';
        /** Icon Set */
        this.iconSet = 'regular';
    }
    get hbClassIconColorSuccess() {
        return this.color === 'success';
    }
    get hbClassIconColorError() {
        return this.color === 'error';
    }
    get hbClassIconColorWarning() {
        return this.color === 'warning';
    }
    get hbClassIconColorInfo() {
        return this.color === 'info';
    }
    get hbClassIconSizeSm() {
        return this.size === 'sm';
    }
    get hbClassIconSizeMd() {
        return this.size === 'md';
    }
    get hbClassIconSizeLg() {
        return this.size === 'lg';
    }
    get hbClassIconSizeXl() {
        return this.size === 'xl';
    }
    get hbClassIconSize2Xl() {
        return this.size === '2xl';
    }
    get hbClassIconSize3Xl() {
        return this.size === '3xl';
    }
    get hbClassIconSizeInherit() {
        return this.size === 'inherit';
    }
    get hbAttrAriaHidden() {
        if (!this.hbAttrAriaLabel) {
            return true;
        }
        return coerceBooleanProperty(this.ariaHidden) ? true : null;
    }
    get hbAttrRole() {
        return this.hbAttrAriaLabel ? 'presentation' : null;
    }
    get hbIconName() {
        return this.icon;
    }
    /** When component loads, check that an icon has been set */
    ngOnInit() {
        if (!this.icon) {
            throw new Error('Icon name is required');
        }
    }
}
IconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
IconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.0", type: IconComponent, isStandalone: true, selector: "vo-icon", inputs: { hbAttrAriaLabel: ["aria-label", "hbAttrAriaLabel"], ariaHidden: ["aria-hidden", "ariaHidden"], size: "size", iconSet: "iconSet", icon: "icon", color: "color" }, host: { properties: { "class.icon": "this.hbClassIcon", "class.icon--color-success": "this.hbClassIconColorSuccess", "class.icon--color-error": "this.hbClassIconColorError", "class.icon--color-warning": "this.hbClassIconColorWarning", "class.icon--color-info": "this.hbClassIconColorInfo", "class.icon--size-sm": "this.hbClassIconSizeSm", "class.icon--size-md": "this.hbClassIconSizeMd", "class.icon--size-lg": "this.hbClassIconSizeLg", "class.icon--size-xl": "this.hbClassIconSizeXl", "class.icon--size-2xl": "this.hbClassIconSize2Xl", "class.icon--size-3xl": "this.hbClassIconSize3Xl", "class.icon--size-inherit": "this.hbClassIconSizeInherit", "attr.aria-label": "this.hbAttrAriaLabel", "attr.aria-hidden": "this.hbAttrAriaHidden", "attr.role": "this.hbAttrRole", "attr.data-icon-name": "this.hbIconName" } }, providers: [
        {
            provide: ICON_COMPONENT,
            useClass: IconComponent,
        },
    ], ngImport: i0, template: "<vo-lazy-awesome\n  data-testid=\"lazy-icon\"\n  [icon]=\"icon\"\n  [iconSet]=\"iconSet\"\n></vo-lazy-awesome>\n", styles: [":host(.icon){--icon-size:1em;--icon-ratio:.75;display:inline-flex;justify-content:center;align-items:center;aspect-ratio:1;box-sizing:content-box;vertical-align:middle;width:var(--icon-size);height:var(--icon-size);font-size:calc(var(--icon-size) * var(--icon-ratio))}:host(.icon--color-success){color:var(--success-bg)}:host(.icon--color-error){color:var(--error-bg-dim)}:host(.icon--color-warning){color:var(--warning-bg)}:host(.icon--color-info){color:var(--info-bg)}:host(.icon--size-sm){--icon-size:1rem}:host(.icon--size-md){--icon-size:1.5rem}:host(.icon--size-lg){--icon-size:2rem}:host(.icon--size-xl){--icon-size:3rem}:host(.icon--size-2xl){--icon-size:4rem}:host(.icon--size-3xl){--icon-size:8rem}:host(.icon--size-inherit){--icon-size:1em}:host(.icon) ::ng-deep svg{padding-bottom:0!important}:host ::ng-deep .ng-fa-icon{display:flex}:host ::ng-deep .svg-inline--fa{display:inline-block;box-sizing:content-box;height:1em;overflow:visible;vertical-align:-.125em}\n"], dependencies: [{ kind: "component", type: LazyAwesomeComponent, selector: "vo-lazy-awesome", inputs: ["icon", "iconSet"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vo-icon', standalone: true, imports: [LazyAwesomeComponent], providers: [
                        {
                            provide: ICON_COMPONENT,
                            useClass: IconComponent,
                        },
                    ], template: "<vo-lazy-awesome\n  data-testid=\"lazy-icon\"\n  [icon]=\"icon\"\n  [iconSet]=\"iconSet\"\n></vo-lazy-awesome>\n", styles: [":host(.icon){--icon-size:1em;--icon-ratio:.75;display:inline-flex;justify-content:center;align-items:center;aspect-ratio:1;box-sizing:content-box;vertical-align:middle;width:var(--icon-size);height:var(--icon-size);font-size:calc(var(--icon-size) * var(--icon-ratio))}:host(.icon--color-success){color:var(--success-bg)}:host(.icon--color-error){color:var(--error-bg-dim)}:host(.icon--color-warning){color:var(--warning-bg)}:host(.icon--color-info){color:var(--info-bg)}:host(.icon--size-sm){--icon-size:1rem}:host(.icon--size-md){--icon-size:1.5rem}:host(.icon--size-lg){--icon-size:2rem}:host(.icon--size-xl){--icon-size:3rem}:host(.icon--size-2xl){--icon-size:4rem}:host(.icon--size-3xl){--icon-size:8rem}:host(.icon--size-inherit){--icon-size:1em}:host(.icon) ::ng-deep svg{padding-bottom:0!important}:host ::ng-deep .ng-fa-icon{display:flex}:host ::ng-deep .svg-inline--fa{display:inline-block;box-sizing:content-box;height:1em;overflow:visible;vertical-align:-.125em}\n"] }]
        }], propDecorators: { hbClassIcon: [{
                type: HostBinding,
                args: ['class.icon']
            }], hbClassIconColorSuccess: [{
                type: HostBinding,
                args: ['class.icon--color-success']
            }], hbClassIconColorError: [{
                type: HostBinding,
                args: ['class.icon--color-error']
            }], hbClassIconColorWarning: [{
                type: HostBinding,
                args: ['class.icon--color-warning']
            }], hbClassIconColorInfo: [{
                type: HostBinding,
                args: ['class.icon--color-info']
            }], hbClassIconSizeSm: [{
                type: HostBinding,
                args: ['class.icon--size-sm']
            }], hbClassIconSizeMd: [{
                type: HostBinding,
                args: ['class.icon--size-md']
            }], hbClassIconSizeLg: [{
                type: HostBinding,
                args: ['class.icon--size-lg']
            }], hbClassIconSizeXl: [{
                type: HostBinding,
                args: ['class.icon--size-xl']
            }], hbClassIconSize2Xl: [{
                type: HostBinding,
                args: ['class.icon--size-2xl']
            }], hbClassIconSize3Xl: [{
                type: HostBinding,
                args: ['class.icon--size-3xl']
            }], hbClassIconSizeInherit: [{
                type: HostBinding,
                args: ['class.icon--size-inherit']
            }], hbAttrAriaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }, {
                type: Input,
                args: ['aria-label']
            }], hbAttrAriaHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }], hbAttrRole: [{
                type: HostBinding,
                args: ['attr.role']
            }], hbIconName: [{
                type: HostBinding,
                args: ['attr.data-icon-name']
            }], ariaHidden: [{
                type: Input,
                args: ['aria-hidden']
            }], size: [{
                type: Input
            }], iconSet: [{
                type: Input
            }], icon: [{
                type: Input
            }], color: [{
                type: Input
            }] } });

class IconLoader {
    constructor() {
        this.library = inject(FaIconLibrary);
        this.iconCache = inject(IconCacheService);
        this.solidIcons = new SolidIconLoader();
        this.regularIcons = new RegularIconLoader();
        this.lightIcons = new LightIconLoader();
    }
    getSetLoader(iconSet) {
        switch (iconSet) {
            case 'regular':
                return this.regularIcons;
            case 'solid':
                return this.solidIcons;
            case 'light':
                return this.lightIcons;
        }
    }
    load(icon, iconSet) {
        const cached = this.iconCache.get(iconSet, icon);
        if (cached) {
            return of(cached);
        }
        const iconSetLoader = this.getSetLoader(iconSet);
        const iconLoader = iconSetLoader[icon];
        if (!iconLoader) {
            throw new Error(`Icon ${icon} not found in icon set ${iconSet}`);
        }
        return from(iconLoader()).pipe(map((data) => data.definition), tap((definition) => this.library.addIcons(definition)), tap((definition) => this.iconCache.set(iconSet, icon, definition)));
    }
}
IconLoader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconLoader, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
IconLoader.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconLoader, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconLoader, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class IconModule {
}
IconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
IconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.0", ngImport: i0, type: IconModule, imports: [IconComponent, CommonModule, FontAwesomeModule], exports: [IconComponent] });
IconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconModule, imports: [IconComponent, CommonModule, FontAwesomeModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [IconComponent, CommonModule, FontAwesomeModule],
                    exports: [IconComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ICON_COMPONENT, IconCacheService, IconComponent, IconLoader, IconModule };
